/**
 * Switch Component
 *
 * A toggle switch for boolean settings.
 */

import { cn } from '@/lib/utils';

interface SwitchProps {
  /** Whether the switch is checked */
  checked: boolean;
  /** Callback when the switch is toggled */
  onCheckedChange: (checked: boolean) => void;
  /** Whether the switch is disabled */
  disabled?: boolean;
  /** Accessible label */
  'aria-label'?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Toggle switch component
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  'aria-label': ariaLabel,
  className
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
        'transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Frog-green when checked (open eye), muted when off (closed)
        checked ? 'bg-primary/30' : 'bg-muted',
        className
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full shadow-lg ring-0',
          // Spring/hop bounce easing for thumb transition (frog hop feel)
          'transition-all duration-200',
          checked ? 'translate-x-5 bg-primary' : 'translate-x-0 bg-background'
        )}
        style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      />
    </button>
  );
}
